import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import { FileText as FileTextIcon, PlusCircle as PlusCircleIcon } from "lucide-react";
import languageLearningApi from "../../middleware/languageLearningApi";
import SentenceItem from "../components/SentenceItem";
import EmptyMessage from "../components/EmptyMessage";
import SentenceForm from "../old/SentenceForm";
import SentenceViewer from "../components/SentenceViewer";

const headerGradient = "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)";

const SentencesPanel = ({ selectedCourseletId, onRefresh }) => {
  const [showSentenceForm, setShowSentenceForm] = useState(false);
  const [sentences, setSentences] = useState([]);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [editSentence, setEditSentence] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (selectedCourseletId) {
      fetchSentences();
    } else {
      setSentences([]);
    }
  }, [selectedCourseletId]);

  const fetchSentences = async () => {
    try {
      const response = await languageLearningApi.get(
        `/sentences/${selectedCourseletId}/sentences`
      );
      const data = response.data;
      if (data.success) {
        setSentences(data.sentences);
      } else {
        setSentences([]);
      }
    } catch (err) {
      console.error("Error fetching sentences:", err);
      setSentences([]);
    }
  };

  // Open the add new sentence form
  const handleAddClick = () => {
    setShowSentenceForm(true);
  };

  // Called when the add/edit sentence form is closed
  const handleFormClose = () => {
    setShowSentenceForm(false);
    fetchSentences();
    if (onRefresh) {
      onRefresh(selectedCourseletId);
    }
  };

  // Close the edit form (after update, if changes were made)
  const handleEditClose = () => {
    setEditSentence(null);
    fetchSentences();
    if (onRefresh) {
      onRefresh(selectedCourseletId);
    }
  };

  // Delete confirmation dialog: confirm deletion
  const handleDeleteConfirm = async () => {
    if (!deleteCandidate) return;
    try {
      const response = await languageLearningApi.delete(
        `/sentences/${deleteCandidate._id}`
      );
      const data = response.data;
      if (data.success) {
        // Remove the deleted sentence from the list
        setSentences(sentences.filter(s => s._id !== deleteCandidate._id));
      }
    } catch (err) {
      console.error("Error deleting sentence:", err);
    }
    setDeleteCandidate(null);
  };

  // Cancel deletion: simply close the confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteCandidate(null);
  };

  return (
    <>
      <Card
        elevation={3}
        sx={{
          overflow: "hidden",
          height: "100%",
          opacity: !selectedCourseletId ? 0.75 : 1
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FileTextIcon size={20} style={{ marginRight: 8 }} />
              <Typography variant="h6" fontWeight="bold">
                Sentences
              </Typography>
            </Box>
          }
          action={
            selectedCourseletId && (
              <IconButton color="primary" onClick={handleAddClick}>
                <PlusCircleIcon size={20} />
              </IconButton>
            )
          }
          sx={{
            background: headerGradient,
            color: "white",
            "& .MuiCardHeader-action": { color: "white" }
          }}
        />
        <CardContent sx={{ p: 2 }}>
          {!selectedCourseletId ? (
            <EmptyMessage
              message="Select a courselet to view sentences"
              showAddButton={false}
            />
          ) : (
            <>
              {showSentenceForm && (
                <SentenceForm
                  courseletId={selectedCourseletId}
                  onClose={handleFormClose}
                />
              )}
              {sentences && sentences.length > 0 ? (
                sentences.map((sentence) => (
                  <SentenceItem
                    key={sentence._id}
                    sentence={sentence}
                    onView={() => setSelectedSentence(sentence)}
                    onEdit={() => setEditSentence(sentence)}
                    onDelete={() => setDeleteCandidate(sentence)}
                  />
                ))
              ) : (
                <EmptyMessage
                  message="No sentences available"
                  showAddButton
                  onAddClick={handleAddClick}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {selectedSentence && (
        <SentenceViewer
          sentence={selectedSentence}
          onClose={() => setSelectedSentence(null)}
        />
      )}

      {editSentence && (
        <SentenceForm
          sentence={editSentence}
          onClose={handleEditClose}
        />
      )}

      <Dialog
        open={Boolean(deleteCandidate)}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the sentence: "{deleteCandidate?.text}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SentencesPanel;
